import { useCallback, useEffect, useState } from 'react';
import { ApiError, getFiles, type FileSummary } from '@/lib/api';

/**
 * One folder's files (FIL-4).
 *
 * The sibling of `useFolders`, keyed one level deeper. Both ids matter: the
 * folder view is a URL (ADR-0007), so moving from one folder to another remounts
 * nothing, and a hook that only fetched on mount would show the previous
 * folder's files under the new folder's name.
 *
 * **This is not authorization.** SEC-5 puts that in the API. What comes back is
 * what the caller may see; the client only renders it.
 *
 * **Follows `continuationToken` to exhaustion before settling `loaded`.** A
 * folder large enough to page used to render only its first page with no sign
 * anything was missing — a table that looked complete while quietly dropping
 * the oldest files, and a total-size figure computed over it would have been
 * wrong in exactly the way `FolderView`'s own summary comment used to warn
 * against. Chaining the requests here, once, is what lets every reader of
 * `state.files` — the table and the folder subtitle both — treat it as the
 * whole folder rather than re-deriving that caveat at each call site.
 */

export type FilesState =
  | { kind: 'loading' }
  /** An empty list is a loaded state — a folder nobody has uploaded into. */
  | { kind: 'loaded'; files: FileSummary[] }
  /** `status` carried for the same reason as in `useFolders`. `0` is network-level. */
  | { kind: 'error'; status: number };

export interface UseFilesResult {
  state: FilesState;
  /** After a failure. Clears back to `loading` first. */
  retry: () => void;
  /**
   * After a mutation — an upload, once that lands.
   *
   * Deliberately does **not** reset to `loading`: the rows on screen are still
   * true while the refetch is in flight.
   */
  refresh: () => void;
}

export function useFiles(spaceId: string, folderId: string): UseFilesResult {
  const [state, setState] = useState<FilesState>({ kind: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    async function loadAll() {
      const files: FileSummary[] = [];
      let continuationToken: string | undefined;

      do {
        const page = await getFiles(spaceId, folderId, continuationToken, controller.signal);
        files.push(...page.files);
        continuationToken = page.continuationToken;
      } while (continuationToken !== undefined);

      return files;
    }

    loadAll()
      .then((files) => {
        setState({ kind: 'loaded', files });
      })
      .catch((error: unknown) => {
        // An abort is our own doing, not a failure.
        if (error instanceof Error && error.name === 'AbortError') return;
        setState({ kind: 'error', status: error instanceof ApiError ? error.status : 0 });
      });

    return () => {
      controller.abort();
    };
  }, [spaceId, folderId, reloadToken]);

  const refresh = useCallback(() => {
    setReloadToken((current) => current + 1);
  }, []);

  // The loading reset belongs in the handler, not the effect: calling setState
  // in an effect body triggers a cascading render.
  const retry = useCallback(() => {
    setState({ kind: 'loading' });
    setReloadToken((current) => current + 1);
  }, []);

  return { state, retry, refresh };
}
